/* eslint-disable no-use-before-define */
/* eslint-disable react/prop-types */
import React, { useEffect } from 'react';
import { useLocation, matchPath } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PerfectScrollbar from 'react-perfect-scrollbar';
import PropTypes from 'prop-types';
import {
  Avatar,
  Box,
  Divider,
  Drawer,
  Hidden,
  Link,
  List,
  ListSubheader,
  Typography,
} from '@material-ui/core';
import {
  Activity as ActivityIcon,
  ShoppingCart as ShoppingCartIcon,
  ShoppingBag as ShoppingBagIcon,
  Settings as SettingsIcon,
  BarChart2 as BarChart2Icon,
  User as UserIcon,
  ArrowRightCircle as HomologatorIcon,
  FileText as FileTextIcon,
} from 'react-feather';

import Logo from '../../Layout/Logo';
import NavItem from './NavItem';

import useStyles from './styles';

const navConfig = [
  {
    subheader: 'Administración',
    items: [
      {
        title: 'Admins',
        icon: UserIcon,
        href: '/administracion/admins',
      },
      {
        title: 'Reportes',
        icon: FileTextIcon,
        href: '/administracion/reportes',
      },
    ],
  },
  {
    subheader: 'Datos',
    items: [
      {
        title: 'Capturas de precios',
        icon: ShoppingCartIcon,
        href: '/datos/productsnapshots',
      },
      {
        title: 'Productos',
        icon: ShoppingBagIcon,
        href: '/datos/productos',
      },
    ],
  },
  {
    subheader: 'Engel',
    items: [
      {
        title: 'Productos Engel',
        icon: HomologatorIcon,
        href: '/engel/engel-products/',
      },
      {
        title: 'Scraper',
        icon: ActivityIcon,
        href: '/engel/scraper',
      },
    ],
  },
  {
    subheader: 'Graficos',
    items: [
      {
        title: 'Precio Promedio',
        icon: BarChart2Icon,
        href: '/graphs/avg-price',
      },
    ],
  },
  {
    subheader: 'Configuraciones',
    items: [
      {
        title: 'Supermercados',
        icon: SettingsIcon,
        href: '/config/supermarkets',
      },
      {
        title: 'Categorias',
        icon: SettingsIcon,
        href: '/config/categories',
      },
      {
        title: 'Subcategorias',
        icon: SettingsIcon,
        href: '/config/subcategories',
      },
      {
        title: 'Alarmas',
        icon: SettingsIcon,
        href: '/config/alarms',
      },
    ],
  },
];

function renderNavItems({ items, ...rest }) {
  return (
    <List disablePadding>
      {items.reduce(
        (acc, item) => reduceChildRoutes({ acc, item, ...rest }),
        [],
      )}
    </List>
  );
}

function reduceChildRoutes({
  acc, pathname, item, depth = 0,
}) {
  const key = item.title + depth;

  if (item.items) {
    const open = matchPath(pathname, {
      path: item.href,
      exact: false,
    });

    acc.push(
      <NavItem
        depth={depth}
        icon={item.icon}
        key={key}
        info={item.info}
        open={Boolean(open)}
        title={item.title}
      >
        {renderNavItems({
          depth: depth + 1,
          pathname,
          items: item.items,
        })}
      </NavItem>,
    );
  } else {
    acc.push(
      <NavItem
        depth={depth}
        href={item.href}
        icon={item.icon}
        key={key}
        info={item.info}
        title={item.title}
      />,
    );
  }

  return acc;
}

function NavBar({ openMobile, onMobileClose }) {
  const classes = useStyles();
  const location = useLocation();
  const account = useSelector((state) => state.account);

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line
  }, [location.pathname]);

  const content = (
    <Box height="100%" display="flex" flexDirection="column">
      <PerfectScrollbar options={{ suppressScrollX: true }}>
        <Hidden lgUp>
          <Box p={2} display="flex" justifyContent="center">
            <RouterLink to="/">
              <Logo className={classes.logo} />
            </RouterLink>
          </Box>
        </Hidden>
        <Box p={2}>
          <Box display="flex" justifyContent="center">
            <RouterLink to="/">
              <Avatar alt="User" className={classes.avatar}/>
            </RouterLink>
          </Box>
          <Box mt={2} textAlign="center">
            <Link
              component={RouterLink}
              to="/"
              variant="h5"
              color="textPrimary"
              underline="none"
            >
              {account.user && account.user.name}
            </Link>
            <Typography variant="body2" color="textSecondary">
              {account.user && account.user.email}
            </Typography>
          </Box>
        </Box>
        <Divider />
        <Box p={2}>
          {navConfig.map((config) => (
            (config.subheader === 'Administración' && account.user.role !== 'admin')
            || (config.subheader === 'Configuraciones' && account.user.role === 'external')
            || (config.subheader === 'Graficos' && account.user.role === 'external')
            || (config.subheader === 'Engel' && account.user.role === 'external')
              ? null
              : <List
                key={config.subheader}
                subheader={(
                  <ListSubheader disableGutters disableSticky>
                    {config.subheader}
                  </ListSubheader>
                )}
              >
              {renderNavItems({
                items: config.items,
                pathname: location.pathname,
              })}
            </List>
          ))}
        </Box>
      </PerfectScrollbar>
    </Box>
  );

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          classes={{ paper: classes.mobileDrawer }}
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Drawer
          anchor="left"
          classes={{ paper: classes.desktopDrawer }}
          open
          variant="persistent"
        >
          {content}
        </Drawer>
      </Hidden>
    </>
  );
}

NavBar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
};

export default NavBar;
